import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { estiloEvento, formatBRL, formatarData, rotuloTipoEvento } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ROTULO_TIPO, ROTULO_TIPO_PLURAL, ehTipoProfissional, type TipoProfissional } from "@/lib/tipoProfissional";

export function Admin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // 24/09: quem está aguardando autorização aparece aqui em cima. Bloqueado
  // não é pedido de acesso e fica fora da conta.
  const usuariosQ = trpc.admin.listarUsuarios.useQuery(undefined, { enabled: isAdmin });
  const aguardando = (usuariosQ.data ?? []).filter(
    (u) => u.role === "pendente" && !u.bloqueadoEm,
  ).length;

  // 24/09: a coordenação de escala ('escala') chega aqui só para tipos de
  // turno e calendário. Painel, usuários, boas práticas e DEV seguem só do
  // admin — e o backend barra 'escala' nessas rotas de qualquer jeito.
  if (!isAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-slate-900">Tipos de turno e calendário</h1>
        <Tabs defaultValue="tipos">
          <TabsList>
            <TabsTrigger value="tipos">Tipos de Turno</TabsTrigger>
            <TabsTrigger value="eventos">Calendário</TabsTrigger>
          </TabsList>
          <TabsContent value="tipos"><TiposTurnoTab /></TabsContent>
          <TabsContent value="eventos"><EventosTab /></TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">Administração</h1>
      {aguardando > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {aguardando === 1 ? "1 pessoa está" : `${aguardando} pessoas estão`} aguardando autorização
          para entrar no RH — veja a aba Usuários.
        </div>
      )}
      <Tabs defaultValue="painel">
        <TabsList>
          <TabsTrigger value="painel">Painel</TabsTrigger>
          <TabsTrigger value="tipos">Tipos de Turno</TabsTrigger>
          <TabsTrigger value="usuarios">Usuários{aguardando > 0 ? ` (${aguardando})` : ""}</TabsTrigger>
          <TabsTrigger value="boas-praticas">Boas práticas</TabsTrigger>
          <TabsTrigger value="eventos">Calendário</TabsTrigger>
          <TabsTrigger value="dev">DEV</TabsTrigger>
        </TabsList>
        <TabsContent value="painel"><PainelTab /></TabsContent>
        <TabsContent value="tipos"><TiposTurnoTab /></TabsContent>
        <TabsContent value="usuarios"><UsuariosTab /></TabsContent>
        <TabsContent value="boas-praticas"><BoasPraticasTab /></TabsContent>
        <TabsContent value="eventos"><EventosTab /></TabsContent>
        <TabsContent value="dev"><DevTab /></TabsContent>
      </Tabs>
    </div>
  );
}

type Secao = { titulo: string; regras: string[] };

function BoasPraticasTab() {
  const q = trpc.configuracoes.obterRegrasPlantao.useQuery();
  const utils = trpc.useUtils();
  const salvar = trpc.configuracoes.salvarRegrasPlantao.useMutation({
    onSuccess: () => {
      utils.configuracoes.obterRegrasPlantao.invalidate();
      alert("Boas práticas salvas com sucesso!");
    },
  });
  const [secoes, setSecoes] = useState<Secao[]>([]);
  const [carregado, setCarregado] = useState(false);

  useEffect(() => {
    if (q.data && !carregado) {
      setSecoes(q.data.secoes.map((s) => ({ titulo: s.titulo, regras: [...s.regras] })));
      setCarregado(true);
    }
  }, [q.data, carregado]);

  function atualizarTitulo(i: number, titulo: string) {
    setSecoes((arr) => arr.map((s, idx) => (idx === i ? { ...s, titulo } : s)));
  }
  function atualizarRegras(i: number, texto: string) {
    const linhas = texto.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
    setSecoes((arr) => arr.map((s, idx) => (idx === i ? { ...s, regras: linhas } : s)));
  }
  function moverSecao(i: number, delta: -1 | 1) {
    const j = i + delta;
    if (j < 0 || j >= secoes.length) return;
    setSecoes((arr) => {
      const novo = [...arr];
      [novo[i], novo[j]] = [novo[j]!, novo[i]!];
      return novo;
    });
  }
  function removerSecao(i: number) {
    if (!confirm("Remover esta seção?")) return;
    setSecoes((arr) => arr.filter((_, idx) => idx !== i));
  }
  function adicionarSecao() {
    setSecoes((arr) => [...arr, { titulo: "Nova orientação", regras: ["Nova sugestão"] }]);
  }
  function descartar() {
    if (!q.data) return;
    if (!confirm("Descartar mudanças não salvas?")) return;
    setSecoes(q.data.secoes.map((s) => ({ titulo: s.titulo, regras: [...s.regras] })));
  }
  function onSalvar() {
    const limpo: Secao[] = secoes
      .map((s) => ({ titulo: s.titulo.trim(), regras: s.regras.filter((r) => r.trim().length > 0) }))
      .filter((s) => s.titulo.length > 0 && s.regras.length > 0);
    salvar.mutate({ secoes: limpo });
  }

  if (q.isLoading) return <div className="p-4 text-sm text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Boas práticas de prestação de serviço</CardTitle>
          <p className="text-sm text-slate-500">
            Sugestões e orientações compartilhadas com a equipe — visíveis no calendário e nas exportações PDF/PNG.
            Use linguagem que respeite a autonomia de profissionais autônomos e pessoas jurídicas:
            prefira <em>"recomenda-se"</em>, <em>"preferencialmente"</em>, <em>"sugere-se"</em> em vez de
            <em> "obrigatório"</em>, <em>"deve"</em>, <em>"tem que"</em>. Edite títulos e itens livremente (um por linha).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {secoes.map((s, i) => (
            <div key={i} className="space-y-2 rounded-md border border-slate-200 p-3">
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1.5">
                  <Label>Título da seção</Label>
                  <Input value={s.titulo} onChange={(e) => atualizarTitulo(i, e.target.value)} />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => moverSecao(i, -1)}
                  disabled={i === 0}
                  title="Mover para cima"
                >↑</Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => moverSecao(i, 1)}
                  disabled={i === secoes.length - 1}
                  title="Mover para baixo"
                >↓</Button>
                <Button size="icon" variant="destructive" onClick={() => removerSecao(i)} title="Remover seção">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-1.5">
                <Label>Sugestões (uma por linha)</Label>
                <textarea
                  className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                  rows={Math.max(3, s.regras.length + 1)}
                  defaultValue={s.regras.join("\n")}
                  onBlur={(e) => atualizarRegras(i, e.target.value)}
                />
                <p className="text-xs text-slate-400">
                  {s.regras.length} sugest{s.regras.length === 1 ? "ão" : "ões"} · alterações sincronizadas ao sair do campo.
                </p>
              </div>
            </div>
          ))}

          <Button variant="outline" onClick={adicionarSecao}>
            <Plus className="mr-2 h-4 w-4" />
            Adicionar seção
          </Button>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={descartar} disabled={salvar.isPending}>
          Descartar mudanças
        </Button>
        <Button onClick={onSalvar} disabled={salvar.isPending}>
          {salvar.isPending ? "Salvando..." : "Salvar tudo"}
        </Button>
      </div>
    </div>
  );
}

function EventosTab() {
  const q = trpc.eventosEspeciais.listarTudo.useQuery({ incluirInativos: true });
  const utils = trpc.useUtils();
  const criar = trpc.eventosEspeciais.criar.useMutation({
    onSuccess: () => {
      utils.eventosEspeciais.listarTudo.invalidate();
      utils.eventosEspeciais.listarPorMes.invalidate();
    },
  });
  const atualizar = trpc.eventosEspeciais.atualizar.useMutation({
    onSuccess: () => {
      utils.eventosEspeciais.listarTudo.invalidate();
      utils.eventosEspeciais.listarPorMes.invalidate();
    },
  });
  const excluir = trpc.eventosEspeciais.excluir.useMutation({
    onSuccess: () => {
      utils.eventosEspeciais.listarTudo.invalidate();
      utils.eventosEspeciais.listarPorMes.invalidate();
    },
  });

  const [nome, setNome] = useState("");
  const [data, setData] = useState("");
  const [tipo, setTipo] = useState<
    "feriado_nacional" | "feriado_estadual" | "feriado_municipal" | "ponto_facultativo" | "aviso"
  >("aviso");
  const [descricao, setDescricao] = useState("");

  function adicionar() {
    if (!nome.trim() || !data) return;
    criar.mutate(
      { nome: nome.trim(), data, tipo, descricao: descricao.trim() || undefined },
      {
        onSuccess: () => {
          setNome("");
          setData("");
          setDescricao("");
          setTipo("aviso");
        },
      },
    );
  }

  const eventos = (q.data ?? []).slice().sort((a, b) => a.data.localeCompare(b.data));
  const futuros = eventos.filter((e) => e.data >= new Date().toISOString().slice(0, 10));
  const passados = eventos.filter((e) => e.data < new Date().toISOString().slice(0, 10));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Calendário — feriados e avisos</CardTitle>
          <p className="text-sm text-slate-500">
            Feriados nacionais, estaduais (SC) e municipais aparecem destacados no calendário de turnos.
            Use o tipo "Aviso" para comunicados pontuais (reunião, treinamento, evento, manutenção etc).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 rounded-md border border-slate-200 p-3 sm:grid-cols-5 sm:items-end">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Nome</Label>
              <Input
                placeholder="Ex: Reunião mensal da equipe"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={tipo} onValueChange={(v) => setTipo(v as typeof tipo)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="feriado_nacional">Feriado nacional</SelectItem>
                  <SelectItem value="feriado_estadual">Feriado estadual</SelectItem>
                  <SelectItem value="feriado_municipal">Feriado municipal</SelectItem>
                  <SelectItem value="ponto_facultativo">Ponto facultativo</SelectItem>
                  <SelectItem value="aviso">Aviso / Evento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={adicionar} disabled={!nome.trim() || !data || criar.isPending}>
              <Plus className="mr-2 h-4 w-4" />
              Adicionar
            </Button>
            <div className="space-y-1.5 sm:col-span-5">
              <Label>Descrição (opcional)</Label>
              <Input
                placeholder="Detalhes que aparecem no tooltip do calendário"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Próximos ({futuros.length})
            </div>
            <ListaEventos
              eventos={futuros}
              onToggleAtivo={(id, ativo) => atualizar.mutate({ id, ativo })}
              onExcluir={(id) => {
                if (confirm("Remover este evento do calendário?")) excluir.mutate({ id });
              }}
            />
          </div>

          {passados.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">
                Passados ({passados.length})
              </summary>
              <div className="mt-2">
                <ListaEventos
                  eventos={passados}
                  onToggleAtivo={(id, ativo) => atualizar.mutate({ id, ativo })}
                  onExcluir={(id) => {
                    if (confirm("Remover este evento do calendário?")) excluir.mutate({ id });
                  }}
                />
              </div>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ListaEventos({
  eventos,
  onToggleAtivo,
  onExcluir,
}: {
  eventos: Array<{ id: number; nome: string; data: string; tipo: string; descricao: string | null; ativo: boolean }>;
  onToggleAtivo: (id: number, ativo: boolean) => void;
  onExcluir: (id: number) => void;
}) {
  if (eventos.length === 0) {
    return <p className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">Sem eventos cadastrados.</p>;
  }
  return (
    <ul className="space-y-2">
      {eventos.map((e) => {
        const est = estiloEvento(e.tipo);
        return (
          <li
            key={e.id}
            className={`flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 p-3 text-sm ${
              e.ativo ? "" : "opacity-50"
            }`}
          >
            <div className="flex flex-1 items-center gap-3">
              <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ring-inset ${est.pillClasse}`}>
                {rotuloTipoEvento(e.tipo)}
              </span>
              <div>
                <div className="font-medium text-slate-900">{e.nome}</div>
                <div className="text-xs text-slate-500">
                  {formatarData(e.data)}
                  {e.descricao && ` · ${e.descricao}`}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => onToggleAtivo(e.id, !e.ativo)}>
                {e.ativo ? "Desativar" : "Ativar"}
              </Button>
              <Button size="icon" variant="destructive" onClick={() => onExcluir(e.id)} title="Remover">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function PainelTab() {
  const q = trpc.admin.painel.useQuery();
  const c = q.data;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Stat label="Usuários" value={c?.usuarios ?? 0} />
        <Stat label="Profissionais ativos" value={c?.profissionaisAtivos ?? 0} />
        <Stat label="Tipos de turno" value={c?.tiposTurnoAtivos ?? 0} />
        <Stat label="Total de turnos" value={c?.totalTurnos ?? 0} />
        <Stat label="Turnos no mês atual" value={c?.turnosMesAtual ?? 0} />
        <Stat label="Solicitações pendentes" value={c?.solicitacoesPendentes ?? 0} />
      </div>
      <Card>
        <CardHeader><CardTitle>Atalhos rápidos</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button asChild variant="outline"><Link to="/calendario">Calendário</Link></Button>
          <Button asChild variant="outline"><Link to="/profissionais">Profissionais</Link></Button>
          <Button asChild variant="outline"><Link to="/solicitacoes">Solicitações</Link></Button>
          <Button asChild variant="outline"><Link to="/remuneracao">Remuneração</Link></Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className="mt-1 text-3xl font-bold text-slate-900">{value}</div>
      </CardContent>
    </Card>
  );
}

type TipoTurno = import("@/types").TipoTurnoListado;

const TIPO_FORM_VAZIO = {
  nome: "",
  tipoProfissional: "auxiliar" as TipoProfissional,
  horaInicio: "08:00",
  horaFim: "12:00",
  valor: "50",
  cor: "#3B82F6",
  ordem: "0",
  descricao: "",
};

// 25/09/2026 — o VALOR do tipo de turno é dinheiro: só o admin define. A
// coordenação de escala cria, edita e ativa tipos sem ver o campo; tipo novo
// criado por ela nasce com R$ 0,00 e aparece como "valor a definir".
const TEXTO_VALOR_SO_ADMIN = "O valor do turno é definido pelo administrador.";

function TiposTurnoTab() {
  const { user } = useAuth();
  const podeValor = user?.role === "admin";
  const q = trpc.tiposTurno.listar.useQuery({ incluirInativos: true });
  const utils = trpc.useUtils();
  const invalidar = () => utils.tiposTurno.listar.invalidate();
  const toggle = trpc.tiposTurno.toggleAtivo.useMutation({ onSuccess: invalidar });
  const criar = trpc.tiposTurno.criar.useMutation({
    onSuccess: (r) => {
      invalidar();
      if (r.aviso) alert(r.aviso);
    },
    onError: (e) => alert(e.message),
  });
  const atualizar = trpc.tiposTurno.atualizar.useMutation({
    onSuccess: (r) => {
      invalidar();
      if (r.aviso) alert(r.aviso);
    },
    onError: (e) => alert(e.message),
  });

  // null = fechado; {} = criando; {id...} = editando
  const [modal, setModal] = useState<null | { editando: TipoTurno | null }>(null);

  const vets = (q.data ?? []).filter(
    (t) => t.tipoProfissional === "veterinario" || t.tipoProfissional === "ambos",
  );
  const auxs = (q.data ?? []).filter(
    (t) => t.tipoProfissional === "auxiliar" || t.tipoProfissional === "ambos",
  );
  // 13/10/2026: secretárias / motorista em grupo próprio (fora de "ambos").
  const apoio = (q.data ?? []).filter((t) => t.tipoProfissional === "apoio");

  const salvando = criar.isPending || atualizar.isPending;

  function salvar(dados: typeof TIPO_FORM_VAZIO) {
    const payloadBase = {
      nome: dados.nome.trim(),
      tipoProfissional: dados.tipoProfissional,
      horaInicio: dados.horaInicio,
      horaFim: dados.horaFim,
      // Só o admin manda valor; para a coordenação o backend ignoraria.
      ...(podeValor ? { valor: Number(dados.valor) } : {}),
      cor: dados.cor,
      ordem: Number(dados.ordem) || 0,
      descricao: dados.descricao.trim() || null,
    };
    if (modal?.editando) {
      atualizar.mutate({ id: modal.editando.id, ...payloadBase }, { onSuccess: () => setModal(null) });
    } else {
      criar.mutate(payloadBase, { onSuccess: () => setModal(null) });
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Tipos de turno</CardTitle>
          <Button size="sm" onClick={() => setModal({ editando: null })}>
            <Plus className="mr-2 h-4 w-4" /> Novo tipo
          </Button>
        </div>
        <p className="text-sm text-slate-500">
          Cada horário/valor diferente é um tipo. O sistema calcula a remuneração pelo valor do tipo.
          Crie, edite ou desative aqui. Desativar não apaga o histórico — só some da lista de novos turnos.
          {!podeValor && ` ${TEXTO_VALOR_SO_ADMIN} Tipo novo nasce com R$ 0,00 até ele definir.`}
        </p>
      </CardHeader>
      <CardContent className="p-2">
        <Accordion type="multiple" defaultValue={["vet", "aux", "apoio"]}>
          <AccordionItem value="vet">
            <AccordionTrigger>Veterinários ({vets.length})</AccordionTrigger>
            <AccordionContent>
              <ListaTipos itens={vets} onToggle={(id) => toggle.mutate({ id })} onEditar={(t) => setModal({ editando: t })} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="aux">
            <AccordionTrigger>Auxiliares ({auxs.length})</AccordionTrigger>
            <AccordionContent>
              <ListaTipos itens={auxs} onToggle={(id) => toggle.mutate({ id })} onEditar={(t) => setModal({ editando: t })} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="apoio">
            <AccordionTrigger>{ROTULO_TIPO_PLURAL.apoio} ({apoio.length})</AccordionTrigger>
            <AccordionContent>
              <ListaTipos itens={apoio} onToggle={(id) => toggle.mutate({ id })} onEditar={(t) => setModal({ editando: t })} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>

      {modal && (
        <TipoTurnoModal
          editando={modal.editando}
          salvando={salvando}
          podeValor={podeValor}
          onClose={() => setModal(null)}
          onSalvar={salvar}
        />
      )}
    </Card>
  );
}

function ListaTipos({
  itens,
  onToggle,
  onEditar,
}: {
  itens: import("@/types").TipoTurnoListado[];
  onToggle: (id: number) => void;
  onEditar: (t: import("@/types").TipoTurnoListado) => void;
}) {
  return (
    <ul className="space-y-2 p-2">
      {itens.map((t) => (
        <li key={t.id} className="flex items-center justify-between rounded-md border border-slate-200 p-3 text-sm">
          <div className="flex items-center gap-3">
            <span className="h-4 w-4 rounded-full" style={{ backgroundColor: t.cor }} />
            <div>
              <div className="font-medium">{t.nome}</div>
              <div className="text-xs text-slate-500">
                {t.horaInicio}–{t.horaFim} · {formatBRL(t.valor)} · ordem {t.ordem}
                {t.ativo && Number(t.valor) === 0 && (
                  <span className="ml-1 font-medium text-amber-700">· valor a definir pelo administrador</span>
                )}
              </div>
              {t.descricao && <div className="text-xs text-slate-500">{t.descricao}</div>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={t.ativo ? "ativo" : "inativo"}>{t.ativo ? "Ativo" : "Inativo"}</Badge>
            <Button size="icon" variant="ghost" className="h-8 w-8" title="Editar" onClick={() => onEditar(t)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => onToggle(t.id)}>
              {t.ativo ? "Desativar" : "Ativar"}
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function TipoTurnoModal({
  editando,
  salvando,
  podeValor,
  onClose,
  onSalvar,
}: {
  editando: import("@/types").TipoTurnoListado | null;
  salvando: boolean;
  /** false = coordenação de escala: o valor aparece travado (só o admin define). */
  podeValor: boolean;
  onClose: () => void;
  onSalvar: (dados: typeof TIPO_FORM_VAZIO) => void;
}) {
  const [form, setForm] = useState<typeof TIPO_FORM_VAZIO>(
    editando
      ? {
          nome: editando.nome,
          tipoProfissional: ehTipoProfissional(editando.tipoProfissional) ? editando.tipoProfissional : "auxiliar",
          horaInicio: editando.horaInicio,
          horaFim: editando.horaFim,
          valor: String(editando.valor),
          cor: editando.cor,
          ordem: String(editando.ordem),
          descricao: editando.descricao ?? "",
        }
      : TIPO_FORM_VAZIO,
  );
  const set = (campo: keyof typeof form, valor: string) =>
    setForm((f) => ({ ...f, [campo]: valor }) as typeof form);

  const horaOk = /^([01]\d|2[0-3]):[0-5]\d$/.test(form.horaInicio) && /^([01]\d|2[0-3]):[0-5]\d$/.test(form.horaFim);
  const valido = form.nome.trim().length > 0 && (!podeValor || Number(form.valor) > 0) && horaOk;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">{editando ? "Editar tipo de turno" : "Novo tipo de turno"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Ex: Aux Noite (16-21)" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Perfil</Label>
              <Select value={form.tipoProfissional} onValueChange={(v) => set("tipoProfissional", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="veterinario">{ROTULO_TIPO.veterinario}</SelectItem>
                  <SelectItem value="auxiliar">{ROTULO_TIPO.auxiliar}</SelectItem>
                  <SelectItem value="ambos">Ambos (vet + auxiliar)</SelectItem>
                  <SelectItem value="apoio">{ROTULO_TIPO.apoio}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Valor (R$)</Label>
              {podeValor ? (
                <Input type="number" step="0.01" min="0" value={form.valor} onChange={(e) => set("valor", e.target.value)} />
              ) : (
                <>
                  <Input disabled value={editando ? formatBRL(editando.valor) : "R$ 0,00 (a definir)"} />
                  <p className="text-xs text-slate-500">{TEXTO_VALOR_SO_ADMIN}</p>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Início</Label>
              <Input type="time" value={form.horaInicio} onChange={(e) => set("horaInicio", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Fim</Label>
              <Input type="time" value={form.horaFim} onChange={(e) => set("horaFim", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Cor</Label>
              <Input type="color" value={form.cor} onChange={(e) => set("cor", e.target.value)} className="h-10 p-1" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Ordem</Label>
              <Input type="number" value={form.ordem} onChange={(e) => set("ordem", e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Descrição (opcional)</Label>
            <Input value={form.descricao} onChange={(e) => set("descricao", e.target.value)} />
          </div>

          {!horaOk && (
            <p className="text-xs text-amber-600">Horário precisa estar no formato HH:MM (ex: 16:00).</p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSalvar(form)} disabled={!valido || salvando}>
            {salvando ? "Salvando..." : editando ? "Salvar" : "Criar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// 24/09/2026 — o pedido do advogado ficou dois dias invisível: a lista não
// separava quem está 'pendente'. Agora quem aguarda vem primeiro, destacado,
// e aprovar é escolher o papel no seletor (grava na hora, como antes).
// O caminho preferido continua sendo a central do ERP (Usuários > Acessos
// centrais), que desde 24/09 recebe estes pedidos e aplica aqui sozinha.
const ROTULO_PAPEL_RH: Record<string, string> = {
  admin: "Admin",
  financeiro: "Financeiro",
  veterinario: "Veterinário",
  auxiliar: "Auxiliar",
  juridico: "Jurídico (só a área Jurídico)",
  escala: "Coordenação de escala (sem RH)",
  pendente: "Pendente (sem acesso)",
};

type PapelRh = "admin" | "financeiro" | "veterinario" | "auxiliar" | "juridico" | "escala" | "pendente";

function UsuariosTab() {
  const { user: eu } = useAuth();
  const q = trpc.admin.listarUsuarios.useQuery();
  const utils = trpc.useUtils();
  const alterar = trpc.admin.alterarRoleUsuario.useMutation({
    onSuccess: () => utils.admin.listarUsuarios.invalidate(),
  });
  // 24/09/2026 — bloqueio de acesso: bloqueado não entra de jeito nenhum
  // (Google, login único do ERP ou sessão antiga) até ser desbloqueado.
  const bloquear = trpc.admin.bloquearUsuario.useMutation({
    onSuccess: () => utils.admin.listarUsuarios.invalidate(),
    onError: (e) => alert(e.message),
  });
  const desbloquear = trpc.admin.desbloquearUsuario.useMutation({
    onSuccess: () => utils.admin.listarUsuarios.invalidate(),
    onError: (e) => alert(e.message),
  });

  const todos = q.data ?? [];
  // Bloqueado não é pedido de acesso: sai de "aguardando" e ganha seção própria.
  const pendentes = todos.filter((u) => u.role === "pendente" && !u.bloqueadoEm);
  const demais = todos.filter((u) => u.role !== "pendente" && !u.bloqueadoEm);
  const bloqueados = todos.filter((u) => !!u.bloqueadoEm);

  function pedirBloqueio(u: (typeof todos)[number]) {
    const motivo = window.prompt(
      `Bloquear o acesso de ${u.name} (${u.email}) ao RH?\n` +
        "A pessoa não entra mais — nem pelo Google, nem pelo login do ERP — até ser " +
        "desbloqueada. Nada é apagado.\n\nMotivo (obrigatório):",
    );
    if (motivo === null) return;
    if (motivo.trim().length < 3) {
      alert("Informe o motivo do bloqueio.");
      return;
    }
    bloquear.mutate({ id: u.id, motivo: motivo.trim() });
  }

  function linha(u: (typeof todos)[number], aguardando: boolean) {
    const bloqueado = !!u.bloqueadoEm;
    return (
      <li
        key={u.id}
        className={
          bloqueado
            ? "flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm"
            : aguardando
            ? "flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
            : "flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 p-3 text-sm"
        }
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 font-semibold text-brand-700">
            {u.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-medium">
              {u.name}
              {aguardando && (
                <Badge className="ml-2 bg-amber-500 text-white">Aguardando autorização</Badge>
              )}
              {bloqueado && <Badge className="ml-2 bg-red-600 text-white">bloqueado</Badge>}
            </div>
            <div className="text-xs text-slate-500">{u.email}</div>
            {aguardando && u.createdAt && (
              <div className="text-xs text-amber-800">
                Pediu acesso em {new Date(u.createdAt).toLocaleString("pt-BR")}
              </div>
            )}
            {bloqueado && (
              <div className="text-xs text-red-800">
                Bloqueado em {new Date(u.bloqueadoEm!).toLocaleString("pt-BR")}
                {u.bloqueadoMotivo ? ` — ${u.bloqueadoMotivo}` : ""}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={u.role}
            onValueChange={(v) => alterar.mutate({ id: u.id, role: v as PapelRh })}
          >
            <SelectTrigger className="w-60"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">{ROTULO_PAPEL_RH.admin}</SelectItem>
              {/* F2: financeiro confere turno e ve Comissoes, sem montar
                  escala. Admin ja engloba financeiro. */}
              <SelectItem value="financeiro">{ROTULO_PAPEL_RH.financeiro}</SelectItem>
              {/* 24/09: monta a escala (turnos, pedidos, tipos, restrições,
                  calendário) e não vê nada de RH, dinheiro ou usuários. */}
              <SelectItem value="escala">{ROTULO_PAPEL_RH.escala}</SelectItem>
              <SelectItem value="veterinario">{ROTULO_PAPEL_RH.veterinario}</SelectItem>
              <SelectItem value="auxiliar">{ROTULO_PAPEL_RH.auxiliar}</SelectItem>
              <SelectItem value="juridico">{ROTULO_PAPEL_RH.juridico}</SelectItem>
              <SelectItem value="pendente">{ROTULO_PAPEL_RH.pendente}</SelectItem>
            </SelectContent>
          </Select>
          {bloqueado ? (
            <Button
              size="sm"
              variant="outline"
              disabled={desbloquear.isPending}
              onClick={() => {
                if (confirm(`Desbloquear ${u.name}? A pessoa volta a entrar com o papel atual.`))
                  desbloquear.mutate({ id: u.id });
              }}
            >
              Desbloquear
            </Button>
          ) : (
            u.id !== eu?.id && (
              <Button
                size="sm"
                variant="destructive"
                disabled={bloquear.isPending}
                onClick={() => pedirBloqueio(u)}
              >
                Bloquear
              </Button>
            )
          )}
        </div>
      </li>
    );
  }

  return (
    <Card>
      <CardHeader><CardTitle>Usuários ({todos.length})</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {pendentes.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-amber-800">
              Aguardando autorização ({pendentes.length})
            </div>
            <p className="text-xs text-slate-600">
              Para liberar, escolha o papel no seletor. O pedido também está na fila da central
              (app.vetcentermiguel.com › Usuários › Acessos centrais) — aprovar por lá aplica aqui
              sozinho e deixa o registro de quem decidiu. "Jurídico" abre só a área Jurídico.
            </p>
            <ul className="space-y-2">{pendentes.map((u) => linha(u, true))}</ul>
          </div>
        )}
        <ul className="space-y-2">{demais.map((u) => linha(u, false))}</ul>
        {bloqueados.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-red-800">
              Bloqueados ({bloqueados.length})
            </div>
            <p className="text-xs text-slate-600">
              Não entram de jeito nenhum — nem pelo Google, nem pelo login único do ERP, nem com
              sessão antiga — até serem desbloqueados. Turnos, extrato e pagamentos continuam
              guardados.
            </p>
            <ul className="space-y-2">{bloqueados.map((u) => linha(u, false))}</ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DevTab() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>Banco de dados</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-sm text-slate-600">
          <div>PostgreSQL 16 + Drizzle ORM</div>
          <div>Database: <code className="text-xs">escalas_db</code></div>
          <div>Tabelas: users, profissionais, tipos_turno, turnos, historico_turnos, solicitacoes_turno, restricoes_profissionais, eventos_especiais, conferencias_mensais</div>
          <div>Views: vw_remuneracao_mensal (previsão), vw_apuracao_mensal (apurado)</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Stack</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-sm text-slate-600">
          <div>React 19 + Vite + Tailwind 4 + shadcn/ui</div>
          <div>Express 4 + tRPC 11</div>
          <div>Google OAuth + JWT (cookie httpOnly)</div>
          <div>Docker + Nginx + Let's Encrypt</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Variáveis de ambiente</CardTitle></CardHeader>
        <CardContent className="space-y-1 font-mono text-xs text-slate-600">
          <div>DATABASE_URL=***</div>
          <div>JWT_SECRET=***</div>
          <div>GOOGLE_CLIENT_ID=***</div>
          <div>GOOGLE_CLIENT_SECRET=***</div>
          <div>FRONTEND_URL=https://escalas.vetcentermiguel.com</div>
        </CardContent>
      </Card>
    </div>
  );
}
